/**
 * Re-indent nested list items so CommonMark sees the hierarchy the source meant.
 *
 * A child list has to start at or past the *content column* of its parent item
 * — column 2 for `* `, column 3 for `1. `. The agent indents children by a
 * single space:
 *
 *     * **Các sản phẩm/dịch vụ:** … bao gồm:
 *      * eci.ins.s-general-2x4
 *
 * One space is short of column 2, so CommonMark makes the child a *sibling* and
 * the whole tree renders as one flat list.
 *
 * Relative indentation is unambiguous even when the absolute amount is too
 * small, so the nesting is rebuilt from it: each item deeper than the one above
 * becomes its child and is re-emitted at that parent's content column. Input
 * that is already well-formed maps to itself.
 */
const LIST_ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])([ \t]+)(.*)$/;
const CODE_FENCE = /^ {0,3}(`{3,}|~{3,})/;
const MATH_FENCE = /^ {0,3}\$\$+(.*)$/;
const THEMATIC_BREAK = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const TAB_WIDTH = 4;

/** Column the line's text starts at, counting a tab as up to four columns. */
const indentWidth = (whitespace: string): number => {
  let width = 0;
  for (const char of whitespace) {
    width = char === "\t" ? width + TAB_WIDTH - (width % TAB_WIDTH) : width + 1;
  }
  return width;
};

type Level = {
  /** Indent the item had in the source, used only to compare depths. */
  source: number;
  /** Column a child of this item must be emitted at. */
  content: number;
};

export const normalizeListIndentation = (markdown: string): string => {
  const lines = markdown.split("\n");
  const out: string[] = [];
  const levels: Level[] = [];
  let inCode = false;
  let inMath = false;

  for (const line of lines) {
    if (!inMath && CODE_FENCE.test(line)) {
      inCode = !inCode;
      out.push(line);
      continue;
    }

    if (!inCode) {
      const math = MATH_FENCE.exec(line);
      // An opening fence's meta may not contain `$`; `$$x$$` is inline math.
      if (math && (inMath || !math[1].includes("$"))) {
        inMath = !inMath;
        out.push(line);
        continue;
      }
    }

    if (inCode || inMath || THEMATIC_BREAK.test(line)) {
      out.push(line);
      continue;
    }

    const item = LIST_ITEM.exec(line);
    if (!item) {
      // A blank line separates items without closing the list; a paragraph
      // flush against the margin ends it.
      if (line.trim() !== "" && indentWidth(/^[ \t]*/.exec(line)![0]) === 0) {
        levels.length = 0;
      }
      out.push(line);
      continue;
    }

    const [, whitespace, marker, spacing, text] = item;
    const source = indentWidth(whitespace);

    while (levels.length > 0 && levels[levels.length - 1].source >= source) {
      levels.pop();
    }

    // With no list open, four spaces is an indented code block, not an item.
    if (levels.length === 0 && source >= TAB_WIDTH) {
      out.push(line);
      continue;
    }

    const indent = levels.length === 0 ? 0 : levels[levels.length - 1].content;
    levels.push({
      source,
      content: indent + marker.length + indentWidth(spacing),
    });
    out.push(" ".repeat(indent) + marker + spacing + text);
  }

  return out.join("\n");
};
